// src/audio/instruments.js
// Instrument registry. Each entry describes how to load a playable instrument.
//
// Western instruments use Tone.Sampler with .ogg samples bundled in
// public/samples/ (originally sourced from
//   https://github.com/nbrosowsky/tonejs-instruments — public domain).
// This lets the app play fully offline after the first load.
//
// Chinese instruments are synthesized via Tone.js (FM for bowed/blown,
// Pluck for zithers) so the project remains license-clean and dependency-free
// for those. Quality is improved over the previous version with vibrato,
// chorus, and a longer release chain.

import * as Tone from 'tone';
import { getLocale } from '../i18n.js';

/**
 * Base path for the sample pack. Vite serves /public at the configured base
 * path, so `{BASE_URL}samples/<instrument>/<note>.ogg` resolves to
 * `${base}samples/<instrument>/<note>.ogg` at runtime.
 */
const SAMPLE_BASE = `${import.meta.env.BASE_URL}samples`;

/**
 * Catalog of available instruments. Two loader types:
 *   - 'sampler' : Tone.Sampler (real instrument samples, bundled locally)
 *   - 'synth'   : synthesized approximation (used for the Chinese family)
 */
export const INSTRUMENT_CATALOG = [
  // ---- Western: Strings (real samples) ----
  { id: 'piano',    name: 'Piano',              nameZh: '钢琴',     family: 'Strings',    familyZh: '弦乐',  type: 'sampler', sample: 'piano',           color: '#e8c547' },
  { id: 'violin',   name: 'Violin',             nameZh: '小提琴',   family: 'Strings',    familyZh: '弦乐',  type: 'sampler', sample: 'violin',          color: '#c25b5b' },
  { id: 'cello',    name: 'Cello',              nameZh: '大提琴',   family: 'Strings',    familyZh: '弦乐',  type: 'sampler', sample: 'cello',           color: '#8a4a6f' },
  { id: 'harp',     name: 'Harp',               nameZh: '竖琴',     family: 'Strings',    familyZh: '弦乐',  type: 'sampler', sample: 'harp',            color: '#7aa8c2' },

  // ---- Western: Winds ----
  { id: 'flute',    name: 'Flute',              nameZh: '长笛',     family: 'Winds',      familyZh: '管乐',  type: 'sampler', sample: 'flute',           color: '#5bbf8a' },
  { id: 'clarinet', name: 'Clarinet',           nameZh: '单簧管',   family: 'Winds',      familyZh: '管乐',  type: 'sampler', sample: 'clarinet',        color: '#6a8eaa' },
  { id: 'trumpet',  name: 'Trumpet',            nameZh: '小号',     family: 'Winds',      familyZh: '管乐',  type: 'sampler', sample: 'trumpet',         color: '#d49a3a' },

  // ---- Western: Plucked ----
  { id: 'guitar',   name: 'Guitar (Nylon)',     nameZh: '吉他 (尼龙)', family: 'Plucked', familyZh: '弹拨',  type: 'sampler', sample: 'guitar-acoustic', color: '#b3855a' },

  // ---- Chinese: sampled (with post-processing) ----
  // guzheng and pipa both reuse the guitar-acoustic .ogg samples,
  // routed through pipa/guzheng-flavoured post-processing chains that
  // shape the nylon-guitar timbre into something distinctly different
  // from the unmodified guitar entry above. Catalog fields:
  //   - sample:           the .ogg folder to load
  //   - release:          overrides the Sampler's release envelope
  //   - postProcessor:    factory returning { head, trigger, dispose }
  { id: 'guzheng',  name: 'Guzheng (古筝)',      nameZh: '古筝',     family: 'Chinese',    familyZh: '中国乐器', type: 'sampler', sample: 'guitar-acoustic', release: 8.0, postProcessor: buildGuzhengPostProcessor, color: '#caa55a' },
  { id: 'pipa',     name: 'Pipa (琵琶)',         nameZh: '琵琶',     family: 'Chinese',    familyZh: '中国乐器', type: 'sampler', sample: 'guitar-acoustic', postProcessor: buildPipaPostProcessor, color: '#c47a3a' }
];

/**
 * Per-instrument sample notes — the FULL set available on the source pack
 * (verified via HTTP probe). More notes means more authentic playback
 * across the keyboard (Tone.Sampler crossfades between adjacent samples
 * to fill the gaps). All files are bundled in public/samples/.
 */
const SAMPLER_NOTES = {
  piano:             ['A1','B1','C1','D1','E1','F1','G1','A2','B2','C2','D2','E2','F2','G2','A3','B3','C3','D3','E3','F3','G3','A4','B4','C4','D4','E4','F4','G4','A5','B5','C5','D5','E5','F5','G5','A6','B6','C6','D6','E6','F6','G6','A7','B7','C7','D7','E7','F7','G7'],
  violin:            ['A3','G3','A4','C4','E4','G4','A5','C5','E5','G5','A6','C6','E6','G6','C7'],
  cello:             ['A2','B2','C2','D2','E2','F2','G2','A3','B3','C3','D3','E3','F3','G3','A4','B4','C4','D4','E4','F4','G4','C5'],
  harp:              ['B1','E1','G1','A2','D2','F2','B3','C3','E3','G3','A4','D4','F4','B5','C5','E5','G5','A6','B6','D6','F6','D7','F7'],
  flute:             ['A4','C4','E4','A5','C5','E5','A6','C6','E6','C7'],
  clarinet:          ['D3','F3','D4','F4','D5','F5','D6'],
  trumpet:           ['A3','F3','C4','F4','G4','A5','D5','F5','C6'],
  'guitar-acoustic': ['A2','B2','D2','E2','F2','G2','A3','B3','C3','D3','E3','F3','G3','A4','B4','C4','D4','E4','F4','G4','C5','D5']
};

const sampleUrl = (instrument, note) =>
  `${SAMPLE_BASE}/${instrument}/${note}.ogg`;

// -----------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------

const instrumentCache = new Map();

/**
 * Build a `urls` object for Tone.Sampler from a list of note names.
 * Tone.Sampler accepts both an array of URLs (one per note) and an object
 * keyed by note. We pass the object form.
 */
function buildSampleUrls(sampleKey) {
  const notes = SAMPLER_NOTES[sampleKey] || ['A4'];
  const urls = {};
  for (const n of notes) urls[n] = sampleUrl(sampleKey, n);
  return urls;
}

/**
 * Load an instrument. Returns a unified instrument object with:
 *   - id, name, family, color, type
 *   - isLoaded: Promise<void> that resolves when the instrument is playable
 *   - output: a Tone.ToneAudioNode for routing
 *   - triggerAttackRelease(pitch, duration, time, velocity)
 *   - triggerAttack(pitch, time, velocity)   // for held/sustained notes
 *   - triggerRelease(pitch, time)           // release a previously attacked note
 *   - dispose()
 *
 * Samplers load asynchronously (Tone.Sampler downloads .ogg files). We
 * expose `isLoaded` as a Promise so callers can await readiness, but the
 * instrument object is returned immediately so the UI thread stays free.
 */
export function loadInstrument(entry, sampleStorage) {
  if (instrumentCache.has(entry.id)) return instrumentCache.get(entry.id);

  const output = new Tone.Gain(1);
  const instrument = {
    id: entry.id,
    name: entry.name,
    family: entry.family,
    color: entry.color,
    type: entry.type,
    output,
    isLoaded: Promise.resolve(),
    triggerAttackRelease: () => {},
    triggerAttack: () => {},
    triggerRelease: () => {},
    dispose: () => output.dispose()
  };

  if (entry.type === 'sampler') {
    const sampler = new Tone.Sampler({
      urls: buildSampleUrls(entry.sample),
      // Catalog can override the default release envelope (useful for
      // long-ringing instruments like guzheng that want 8+ s tails).
      release: typeof entry.release === 'number' ? entry.release : 1.2
      // No `baseUrl` — `buildSampleUrls` returns absolute URLs so Tone.Sampler
      // fetches them directly. Passing both would cause it to URL-encode the
      // absolute URL and append it to baseUrl, producing broken requests.
    });
    // If the catalog entry declares a postProcessor, the sampler feeds
    // through that chain instead of connecting straight to `output`.
    // postChain is `{ head, trigger }` — `head` is the routable node
    // TrackManager will connect, and `trigger(time, velocity)` is fired
    // per-note from the wrapper's triggerAttackRelease below.
    const postChain = typeof entry.postProcessor === 'function'
      ? entry.postProcessor()
      : null;
    const samplerTail = postChain ? postChain.head : output;
    sampler.connect(samplerTail);
    instrument.output = samplerTail; // re-bind so TrackManager connects to the chain head

    // Track every ToneBufferSource the Sampler creates, including
    // future-scheduled ones (where start() has been called with a time
    // in the future, but the source hasn't fired yet). The Sampler's
    // built-in _activeSources / releaseAll() can only fade these out,
    // not cancel them — so on reset we walk our own list and dispose
    // each source, which disconnects the underlying AudioBufferSourceNode
    // before the clock ever reaches its start time.
    const trackedSources = new Set();
    // Params captured at trigger time, keyed by source. Lets
    // instrument.pauseSchedule() rebuild the schedule on resume after
    // disposing the sources (which is necessary because the AudioContext
    // clock keeps running through Transport.pause()).
    const sourceParams = new Map();
    const _origTriggerAttack = sampler.triggerAttack.bind(sampler);
    sampler.triggerAttack = (notes, time, velocity = 1) => {
      // Snapshot the size of every midi-bucket in _activeSources so we
      // can identify the sources added by this call.
      const before = new Map();
      for (const [midi, arr] of sampler._activeSources) {
        before.set(midi, arr.length);
      }
      _origTriggerAttack(notes, time, velocity);
      // Any new sources appended to existing buckets are ours.
      // `notes` may be a single frequency or an array; normalise so we
      // can store one entry per source.
      const noteList = Array.isArray(notes) ? notes : [notes];
      for (const noteOrFreq of noteList) {
        // The Sampler buckets by MIDI; re-derive MIDI from the freq.
        const midi = Math.round(69 + 12 * Math.log2(noteOrFreq / 440));
        const arr = sampler._activeSources.get(midi);
        if (!arr) continue;
        const prev = before.get(midi) || 0;
        for (let i = prev; i < arr.length; i++) {
          const src = arr[i];
          trackedSources.add(src);
          // Remember the params so we can re-trigger on resume.
          sourceParams.set(src, { midi, time, velocity });
          // When the source ends naturally, drop it from our tracker.
          const prevOnended = src.onended;
          src.onended = (e) => {
            if (typeof prevOnended === 'function') prevOnended(e);
            trackedSources.delete(src);
            sourceParams.delete(src);
          };
        }
      }
      return sampler;
    };

    /**
     * Hard-cancel every scheduled source (active AND future-scheduled).
     * Disposes the underlying AudioBufferSourceNode so the audio never
     * plays. Use this on reset, where preserving the schedule isn't
     * desired. (For pause-style stop, releaseAll is the right tool.)
     */
    instrument.cancelAllScheduled = () => {
      for (const src of trackedSources) {
        try { src.dispose(); } catch { /* noop */ }
      }
      trackedSources.clear();
      sourceParams.clear();
      // Drop any snapshot left over from a previous pause — after a hard
      // cancel there's no schedule to resume, and stale params would
      // leak memory and confuse the next resumeSchedule() call.
      instrument._pausedSchedule = null;
    };

    /**
     * Snapshot every scheduled source and dispose it. Returns an array
     * of `{ midi, time, velocity }` so the caller (TrackManager.pauseAll)
     * can re-trigger them at adjusted times on resume. The schedule
     * snapshot itself lives on the wrapper (`_pausedSchedule`) so the
     * resume path doesn't need to thread the data through arguments.
     *
     * This is necessary because `Tone.Transport.pause()` does NOT stop
     * the AudioContext clock — every ToneBufferSource that was scheduled
     * with `start(time)` will still fire at its scheduled AudioContext
     * time even after pause. Disposing the source is the only way to
     * stop it from playing.
     */
    instrument.pauseSchedule = () => {
      const snapshot = [];
      for (const src of trackedSources) {
        const params = sourceParams.get(src);
        if (!params) continue;
        snapshot.push({ ...params });
        try { src.dispose(); } catch { /* noop */ }
      }
      trackedSources.clear();
      sourceParams.clear();
      instrument._pausedSchedule = snapshot;
      return snapshot;
    };

    /**
     * Re-trigger every source that pauseSchedule() snapshot'd. The
     * caller passes a `timeShift` (seconds) — typically `now - pauseTime`
     * — which is added to each source's original scheduled time so the
     * notes still land at the right arrangement positions relative to
     * the resumed transport clock.
     */
    instrument.resumeSchedule = (timeShift = 0) => {
      const snapshot = instrument._pausedSchedule;
      if (!snapshot || snapshot.length === 0) return;
      instrument._pausedSchedule = null;
      for (const { midi, time, velocity } of snapshot) {
        const freq = midiToFrequency(midi);
        try {
          sampler.triggerAttack(freq, time + timeShift, velocity);
        } catch { /* noop */ }
      }
    };

    instrument.isLoaded = new Promise((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      // If the Sampler is already loaded (cached), resolve right away.
      if (sampler._buffers && sampler._buffers.loaded) {
        settle();
        return;
      }
      // Poll the Sampler's internal buffers.loaded flag. The Sampler
      // doesn't expose a settable onload callback, so polling the
      // aggregate `loaded` getter is the most reliable signal. A 30s
      // safety timeout guarantees we never hang forever if a
      // decodeAudioData call neither resolves nor rejects.
      const SAMPLE_LOAD_TIMEOUT_MS = 30000;
      const timeout = setTimeout(() => {
        if (!settled) {
          // eslint-disable-next-line no-console
          console.warn(
            `[helix-composer] Sampler "${entry.id}" load timed out after ` +
            `${SAMPLE_LOAD_TIMEOUT_MS / 1000}s; resolving isLoaded anyway.`
          );
          settle();
        }
      }, SAMPLE_LOAD_TIMEOUT_MS);
      const poll = () => {
        if (settled) return;
        if (sampler._buffers && sampler._buffers.loaded) {
          clearTimeout(timeout);
          settle();
          return;
        }
        requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    });
    instrument.triggerAttackRelease = (pitch, duration, time, velocity = 0.8) => {
      const freq = midiToFrequency(pitch);
      // Tone.Sampler passes the velocity through to ToneBufferSource.start
      // as a linear GAIN (0..1, default 1). The previous code computed a
      // dB value here and passed it as velocity, which (a) clamps to 0 for
      // any velocity < 1 (since dB is negative) making most notes silent,
      // and (b) destroys the natural dynamics curve. Pass velocity as a
      // clamped linear gain.
      const gain = Math.max(0, Math.min(1, velocity));
      try {
        sampler.triggerAttackRelease(freq, Math.max(0.05, duration), time, gain);
      } catch (err) {
        // Sampler may not be ready yet; suppress silently.
      }
      // If a postProcessor was supplied (e.g. pipa's timbre chain),
      // give it a per-note tick to fire any envelopes it owns.
      if (postChain && typeof postChain.trigger === 'function') {
        try { postChain.trigger(time, gain); } catch { /* noop */ }
      }
    };
    // Sustain pair: start a ringing note, release it later. The custom
    // sampler.triggerAttack override above tracks the source in
    // `trackedSources` so cancelAllScheduled() will dispose it on stop.
    instrument.triggerAttack = (pitch, time, velocity = 0.8) => {
      const freq = midiToFrequency(pitch);
      const gain = Math.max(0, Math.min(1, velocity));
      try {
        sampler.triggerAttack(freq, time, gain);
      } catch { /* noop */ }
      if (postChain && typeof postChain.trigger === 'function') {
        try { postChain.trigger(time, gain); } catch { /* noop */ }
      }
    };
    instrument.triggerRelease = (pitch, time) => {
      const freq = midiToFrequency(pitch);
      try {
        sampler.triggerRelease(freq, time);
      } catch { /* noop */ }
    };
    // The wrapper does not expose `release` directly (it lives on the
    // inner Tone.Sampler). TrackManager.stopAll() needs to shorten it
    // for fast fade-out on pause/stop, so expose it via this setter.
    // Returns the previous value so callers can restore it.
    instrument.setRelease = (value) => {
      const prev = sampler.release;
      sampler.release = value;
      return prev;
    };
    instrument.dispose = () => {
      // Cancel all sources first (this disposes their underlying
      // AudioBufferSourceNodes) before disposing the Sampler (which
      // also disposes the buffers).
      instrument.cancelAllScheduled();
      try { sampler.dispose(); } catch { /* noop */ }
      if (postChain && typeof postChain.dispose === 'function') {
        try { postChain.dispose(); } catch { /* noop */ }
      }
      output.dispose();
      // Evict from the cache so a future loadInstrument(entry) builds
      // a fresh Sampler. Without this, after dispose the next caller
      // would get the dead instrument back and any note triggers
      // would silently fail (no sound, no error).
      if (instrumentCache.get(entry.id) === instrument) {
        instrumentCache.delete(entry.id);
      }
    };
  } else if (entry.type === 'synth') {
    const chain = entry.synthFactory();
    chain.connect(output);
    const synth = chain._helixSynth;
    instrument.triggerAttackRelease = (pitch, duration, time, velocity = 0.8) => {
      if (synth && synth.triggerAttackRelease) {
        try {
          synth.triggerAttackRelease(pitch, Math.max(0.05, duration), time, velocity);
        } catch (err) { /* noop */ }
      }
    };
    // Sustain pair for synths. Tone.js synths accept either a MIDI
    // number or a frequency string for note names, so we pass the pitch
    // straight through (the sampler path converts to frequency because
    // its internal buffers are keyed by note name).
    instrument.triggerAttack = (pitch, time, velocity = 0.8) => {
      if (synth && synth.triggerAttack) {
        try { synth.triggerAttack(pitch, time, velocity); } catch { /* noop */ }
      }
    };
    instrument.triggerRelease = (pitch, time) => {
      if (synth && synth.triggerRelease) {
        try { synth.triggerRelease(pitch, time); } catch { /* noop */ }
      }
    };
    // Synths expose `release` directly via the wrapper, so setting
    // `instrument.release = …` works in TrackManager.stopAll(). But
    // expose the setter as well for parity with the sampler path so
    // stopAll() can use a single code path. Returns the previous value.
    instrument.setRelease = (value) => {
      let prev = 0.01;
      try {
        prev = synth.release;
        synth.release = value;
      } catch { /* noop */ }
      return prev;
    };
    instrument.isLoaded = Promise.resolve();
    // Synths trigger their voices synchronously — there are no
    // future-scheduled sources to cancel. releaseAll() (called by
    // TrackManager.stopAll) is enough to silence them.
    instrument.cancelAllScheduled = () => {
      if (synth && typeof synth.releaseAll === 'function') {
        try { synth.releaseAll(Tone.now()); } catch { /* noop */ }
      }
    };
    instrument.dispose = () => {
      if (synth) {
        try { synth.dispose(); } catch { /* noop */ }
      }
      try { chain.dispose(); } catch { /* noop */ }
      output.dispose();
      // Evict from the cache so a future loadInstrument(entry) builds
      // a fresh synth. See the Sampler dispose() above for the rationale.
      if (instrumentCache.get(entry.id) === instrument) {
        instrumentCache.delete(entry.id);
      }
    };
  } else if (entry.type === 'custom') {
    // Custom sampler built from user-imported audio samples stored in IndexedDB.
    // We set up isLoaded to fetch and decode all samples asynchronously, then
    // create the Tone.Sampler once all AudioBuffers are ready.
    instrument.isLoaded = (async () => {
      const urls = {};
      if (!sampleStorage) {
        console.warn(`[instruments] Custom instrument "${entry.id}" has no SampleStorage — skipping sample load`);
        instrument.isReady = false;
        return;
      }
      const decodePromises = [];
      for (const [note, ref] of Object.entries(entry.sampleMap || {})) {
        const p = sampleStorage.getSample(ref).then(async (arrayBuffer) => {
          if (!arrayBuffer) return;
          try {
            const audioBuffer = await Tone.context.decodeAudioData(arrayBuffer.slice(0));
            urls[note] = audioBuffer;
          } catch (e) {
            console.warn(`[instruments] Failed to decode sample ${ref} for note ${note}:`, e);
          }
        });
        decodePromises.push(p);
      }
      await Promise.all(decodePromises);

      if (Object.keys(urls).length === 0) {
        console.warn(`[instruments] Custom instrument "${entry.id}" has no loadable samples`);
        instrument.isReady = false;
        return;
      }

      const sampler = new Tone.Sampler({
        urls,
        release: typeof entry.release === 'number' ? entry.release : 1.2
      });
      sampler.connect(output);

      // Wire up triggerAttackRelease — same pattern as sampler branch
      instrument.triggerAttackRelease = (pitch, duration, time, velocity = 0.8) => {
        const freq = 440 * Math.pow(2, (pitch - 69) / 12);
        const gain = Math.max(0, Math.min(1, velocity));
        try { sampler.triggerAttackRelease(freq, Math.max(0.05, duration), time, gain); } catch {}
      };
      instrument.triggerAttack = (pitch, time, velocity = 0.8) => {
        const freq = 440 * Math.pow(2, (pitch - 69) / 12);
        const gain = Math.max(0, Math.min(1, velocity));
        try { sampler.triggerAttack(freq, time, gain); } catch {}
      };
      instrument.triggerRelease = (pitch, time) => {
        const freq = 440 * Math.pow(2, (pitch - 69) / 12);
        try { sampler.triggerRelease(freq, time); } catch {}
      };
      instrument.setRelease = (value) => {
        const prev = sampler.release;
        sampler.release = value;
        return prev;
      };
      instrument.cancelAllScheduled = () => {
        try { sampler.releaseAll(Tone.now()); } catch {}
      };
      instrument.dispose = () => {
        try { sampler.dispose(); } catch {}
        output.dispose();
        if (instrumentCache.get(entry.id) === instrument) instrumentCache.delete(entry.id);
      };
      instrument.isReady = true;
    })().catch(err => {
      console.warn(`[instruments] Custom instrument "${entry.id}" loading failed:`, err);
      instrument.isReady = false;
    });
  }

  instrumentCache.set(entry.id, instrument);
  return instrument;
}

/**
 * Resolve an instrument by ID, checking built-in catalog first,
 * then the custom instrument registry. Returns the entry object
 * or null if not found.
 * @param {string} id
 * @param {import('../instruments/CustomInstrumentRegistry.js').CustomInstrumentRegistry} [customRegistry]
 */
export function resolveInstrument(id, customRegistry) {
  const builtin = INSTRUMENT_CATALOG.find(e => e.id === id);
  if (builtin) return builtin;
  if (customRegistry) return customRegistry.get(id);
  return null;
}

export function getInstrumentEntry(id, customRegistry) {
  return resolveInstrument(id, customRegistry);
}

/**
 * Return the localized name for an instrument catalog entry.
 * Uses the `nameZh` field when the current locale is 'zh',
 * otherwise the standard `name` field.
 */
export function getInstrumentName(entry) {
  if (!entry) return '';
  return getLocale() === 'zh' ? (entry.nameZh || entry.name) : entry.name;
}

/**
 * Return the localized family label for an instrument catalog entry.
 * Uses the `familyZh` field when the current locale is 'zh',
 * otherwise the standard `family` field.
 */
export function getInstrumentFamily(entry) {
  if (!entry) return '';
  return getLocale() === 'zh' ? (entry.familyZh || entry.family) : entry.family;
}

/** MIDI note number -> frequency in Hz (equal temperament, A4=440). */
function midiToFrequency(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

// -----------------------------------------------------------------------
// Chinese instrument synth builders
// Each builder returns the HEAD of the signal chain; the underlying Tone
// synth is exposed via `head._helixSynth` so we can call trigger on it.
// -----------------------------------------------------------------------

function buildErhu() {
  // Bowed string: FM with a strong modulator + slow vibrato.
  const synth = new Tone.FMSynth({
    harmonicity: 2.0,
    modulationIndex: 6,
    oscillator: { type: 'sawtooth' },
    modulation: { type: 'sine' },
    envelope: { attack: 0.12, decay: 0.4, sustain: 0.8, release: 0.6 },
    modulationEnvelope: {
      attack: 0.08, decay: 0.3, sustain: 0.6, release: 0.4
    }
  });
  // Vibrato via a slow LFO modulating the synth's frequency.
  const vibrato = new Tone.LFO('5.5Hz', -25, 25).start();
  vibrato.connect(synth.frequency);

  const filter = new Tone.Filter(2400, 'lowpass');
  const chorus = new Tone.Chorus({ frequency: 1.5, delayTime: 3.5, depth: 0.7, wet: 0.3 }).start();

  synth.chain(filter, chorus);
  chorus._helixSynth = synth;
  return chorus;
}

function buildGuzhengPostProcessor() {
  // Post-processing chain that turns the guitar-acoustic Sampler into
  // something guzheng-flavoured. The signature guzheng sound has:
  //   1. An exceptionally long, singing sustain (8–30 s on a real
  //      instrument, sustained by sympathetic resonance of the other
  //      strings). The Sampler's release envelope is set to 8 s on
  //      the catalog entry; we add a feedback delay that recycles
  //      energy and an extended reverb tail for further ringing.
  //   2. A bright, bell-like top end — the bridges transfer high
  //      harmonics back into the body. EQ'd with a peak around 5 kHz.
  //   3. Slight chorus and ensemble wash to mimic multiple strings
  //      ringing sympathetically.
  //   4. Pentatonic-friendly tuning: the guitar-acoustic samples are
  //      equal-temperament so this is automatic; we don't need to
  //      retune, just not add anything that fights the tuning.
  //
  // Returns `{ head, trigger, dispose }` like the pipa chain.

  // A. Gentle highpass to keep mud out without killing body.
  const highpass = new Tone.Filter(120, 'highpass');

  // B. Brilliance peak around 5 kHz — the guzheng's bell-like sheen.
  //    EQ3 with mid band would be ideal but Tone.EQ3 doesn't expose
  //    the same way; a peakingFilter is simpler.
  const brilliance = new Tone.Filter(5000, 'peaking', -12);
  brilliance.gain.value = 6; // +6 dB at 5 kHz
  brilliance.Q.value = 0.8;

  // C. Slight low-shelf cut to keep things from getting boomy on
  //    held notes.
  const lowCut = new Tone.Filter(180, 'lowshelf');
  lowCut.gain.value = -2;

  // D. Chorus for the ensemble wash. Slow, low depth — a subtle
  //    thickening rather than obvious detuning.
  const chorus = new Tone.Chorus({
    frequency: 0.6,
    delayTime: 4.5,
    depth: 0.4,
    wet: 0.3
  }).start();

  // E. Feedback delay — recycles note energy with a long decay so
  //    held notes keep ringing well past the Sampler's release. We
  //    tap the delayed signal back into the input at 0.45 gain; with
  //    a delay of 1.8 s this gives a perceptible echo that decays
  //    over many seconds.
  const feedbackDelay = new Tone.FeedbackDelay({
    delayTime: 1.8,
    feedback: 0.45,
    wet: 0.35
  });

  // F. Reverb — adds the spatial wash. We use Tone.Reverb with a
  //    long decay and modest wet; it sits behind the delay so it
  //    doesn't muddy the direct signal.
  const reverb = new Tone.Reverb({
    decay: 5.0,
    preDelay: 0.02,
    wet: 0.25
  });

  // G. Output gain. Slightly under unity because the chain stacks
  //    multiple boosts (EQ + chorus + delay + reverb).
  const head = new Tone.Gain(0.85);

  // Signal path:
  //   highpass → lowshelf → brilliance → chorus → feedbackDelay →
  //     reverb → head
  // Feedback is from feedbackDelay's wet output back into its input
  // (FeedbackDelay handles that internally via the `feedback` param).
  highpass.connect(lowCut);
  lowCut.connect(brilliance);
  brilliance.connect(chorus);
  chorus.connect(feedbackDelay);
  feedbackDelay.connect(reverb);
  reverb.connect(head);

  return {
    head,
    // Guzheng has no per-note envelope — the Sampler's release and
    // the feedback delay handle the long sustain. trigger() is a
    // no-op kept for API parity with buildPipaPostProcessor.
    trigger(_time, _velocity = 0.8) { /* noop — Sampler release handles envelope */ },
    dispose() {
      try { highpass.dispose(); } catch { /* noop */ }
      try { brilliance.dispose(); } catch { /* noop */ }
      try { lowCut.dispose(); } catch { /* noop */ }
      try { chorus.dispose(); } catch { /* noop */ }
      try { feedbackDelay.dispose(); } catch { /* noop */ }
      try { reverb.dispose(); } catch { /* noop */ }
      try { head.dispose(); } catch { /* noop */ }
    }
  };
}

function buildPipaPostProcessor() {
  // Aggressive post-processing chain that turns the guitar-acoustic
  // Sampler into something pipa-flavoured. The Sampler feeds through
  // a highpass → narrow metallic bandpass → nasal resonance peak →
  // waveshaper → head, with a per-note transient noise burst fired
  // from `trigger(time, velocity)` for the percussive attack.
  //
  // Returns `{ head, trigger }` so loadInstrument can wire
  // sampler → head → output and fire `trigger(time, velocity)` from
  // the wrapper's triggerAttackRelease.

  // 1. Cut low body — pipa has almost no fundamental; everything below
  //    ~2.5 kHz is mostly sympathetic noise. Higher cutoff than v1.
  const highpass = new Tone.Filter(2500, 'highpass');

  // 2. Narrow metallic bandpass. Centre is swept up to ~7 kHz on
  //    attack by a FrequencyEnvelope (octaves > 1) and settles to
  //    ~5 kHz — the pipa's bright metallic ring sits in this range.
  //    Q=6 gives the narrow filter character the user asked for.
  const metallic = new Tone.Filter(5000, 'bandpass');
  metallic.Q.value = 6;

  const metallicEnv = new Tone.FrequencyEnvelope({
    attack: 0.003,
    decay: 0.04,
    sustain: 0.6,
    release: 0.08,
    baseFrequency: 5000,
    octaves: 0.5 // sweep up to ~7 kHz on attack, settle to ~5 kHz
  });
  metallicEnv.connect(metallic.frequency);

  // 3. Nasal resonance peak — a second narrow bandpass around 1.6 kHz
  //    mixed in parallel to add the "honk" the user described. Mixed
  //    lightly so it colours rather than dominates.
  const nasal = new Tone.Filter(1600, 'bandpass');
  nasal.Q.value = 4;
  const nasalLevel = new Tone.Gain(0.35);

  // 4. Aggressive waveshaper — sharper curve adds upper harmonics
  //    (the metallic bite) and compresses the dynamics. The curve is
  //    asymmetric — more compression on positive half — which adds
  //    odd harmonics only.
  const waveshaper = new Tone.WaveShaper(
    new Float32Array([-0.8, -0.5, -0.2, 0, 0.4, 0.7, 0.85, 0.9, 0.95])
  );

  // 5. Final mix and output. The waveshaper is fed by both the
  //    metallic path and the nasal path summed, so the saturation
  //    hits both frequency bands.
  const mix = new Tone.Gain(1);
  const head = new Tone.Gain(0.9);

  highpass.connect(metallic);
  metallic.connect(mix);
  nasal.connect(nasalLevel);
  nasalLevel.connect(mix);
  mix.connect(waveshaper);
  waveshaper.connect(head);

  // 6. Per-note transient noise burst — the "fingernail snap" that
  //    gives pipa its percussive attack. White noise bandpass'd at
  //    ~6 kHz, gated by a fast envelope fired from trigger().
  const clickNoise = new Tone.Noise('white').start();
  const clickFilter = new Tone.Filter(6000, 'bandpass');
  clickFilter.Q.value = 2;
  const clickEnv = new Tone.Gain(0);
  clickNoise.connect(clickFilter);
  clickFilter.connect(clickEnv);
  // Click mixes in alongside the main signal, pre-waveshaper so the
  // saturation hits it too.
  clickEnv.connect(mix);

  return {
    head,
    trigger(time, velocity = 0.8) {
      const vel = Math.max(0, Math.min(1, velocity));
      try { metallicEnv.triggerAttackRelease(0.2, time, vel); } catch { /* noop */ }
      // Transient click: very short burst, level scales with velocity.
      try {
        const clickLevel = 0.35 + vel * 0.55;
        const dur = 0.025; // 25ms — fast attack/decay
        clickEnv.gain.setValueAtTime(0, time);
        clickEnv.gain.linearRampToValueAtTime(clickLevel, time + 0.002);
        clickEnv.gain.exponentialRampToValueAtTime(0.0001, time + dur);
      } catch { /* noop */ }
    },
    dispose() {
      try { highpass.dispose(); } catch { /* noop */ }
      try { metallic.dispose(); } catch { /* noop */ }
      try { metallicEnv.dispose(); } catch { /* noop */ }
      try { nasal.dispose(); } catch { /* noop */ }
      try { nasalLevel.dispose(); } catch { /* noop */ }
      try { waveshaper.dispose(); } catch { /* noop */ }
      try { mix.dispose(); } catch { /* noop */ }
      try { head.dispose(); } catch { /* noop */ }
      try { clickNoise.dispose(); } catch { /* noop */ }
      try { clickFilter.dispose(); } catch { /* noop */ }
      try { clickEnv.dispose(); } catch { /* noop */ }
    }
  };
}

function buildDizi() {
  // Dizi: FMSynth with bright modulation, plus a small breath noise component.
  const synth = new Tone.FMSynth({
    harmonicity: 1.0,
    modulationIndex: 3,
    oscillator: { type: 'triangle' },
    modulation: { type: 'sine' },
    envelope: { attack: 0.04, decay: 0.15, sustain: 0.85, release: 0.3 },
    modulationEnvelope: {
      attack: 0.02, decay: 0.1, sustain: 0.5, release: 0.2
    }
  });
  // Subtle vibrato
  const vibrato = new Tone.LFO('5Hz', -15, 15).start();
  vibrato.connect(synth.frequency);

  // Breath noise: white noise burst that swells with each note via an envelope follower would be ideal,
  // but a simpler approach is a static noise pad that's mixed in at low level.
  const noise = new Tone.Noise('white');
  const noiseFilter = new Tone.Filter(4000, 'bandpass');
  const noiseGain = new Tone.Gain(0.04);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noise.start();

  const filter = new Tone.Filter(6000, 'lowpass');
  const gain = new Tone.Gain(0.9);
  synth.chain(filter, gain);
  noiseGain.connect(gain);
  gain._helixSynth = synth;
  return gain;
}
